import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { usePercentSerialiser } from "hooks/serialisers/percent";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { useTenantTranslation } from "hooks/formatters";
import {
  HeatsSummaryItem as HeatsSummaryItemType,
  RatioRecipeAggregate,
} from "src/store/api/generatedApi";
import { useMemo } from "react";
import { sorted } from "helpers";
import { useHeatsData } from "components/strategyDashboard/heats/context/HeatsDataContext";
import {
  TrafficLight,
  getTrafficLightColor,
} from "components/strategyDashboard/heats/cells/TrafficLight";
import { useHeatsFilter } from "../context/HeatsFilterContext";

interface HeatsSummaryRecipeItemProps {
  item: HeatsSummaryItemType;
}

/**
 * Renders a daily summary recipe (e.g., average recipe by steel grade or average lifts per material)
 */
export const HeatsSummaryRecipeItem = ({
  item,
}: HeatsSummaryRecipeItemProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const { ignoredMaterials } = useHeatsFilter();
  const { materials: allMaterials } = useHeatsData();
  const values = item.value;

  const sortedValues: RatioRecipeAggregate[] = useMemo(
    () =>
      Array.isArray(values) && allMaterials
        ? sorted(
            values,
            (key) =>
              allMaterials.find((m) => m.name === key.material)?.ordering ?? 0
          )
        : Array.isArray(values)
          ? values
          : [],
    [values, allMaterials]
  );
  const shortNames = Object.fromEntries(
    allMaterials?.map((m) => [m.name, m.short_name]) ?? []
  );

  const strikeCount = useMemo(
    () => sortedValues.filter((v) => v.is_strike).length,
    [sortedValues]
  );

  const useNumberFormat = item.unit_type === null;
  const { format: formatPercent } = usePercentSerialiser({
    decimalPlaces: item.decimal_places,
    includeSymbol: true,
  });
  const { format: formatNumber } = useNumberSerialiser({
    decimalPlaces: item.decimal_places,
  });
  const format = useNumberFormat ? formatNumber : formatPercent;

  if (!Array.isArray(item.value)) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <TrafficLight color={getTrafficLightColor(strikeCount)} size="1rem" />
        <Typography
          variant="body2"
          sx={{
            fontWeight: "fontWeightMedium",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {t(item.summary_name)}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {sortedValues.map((v) => (
          <Tooltip key={v.material} title={v.material}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: 0,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "fontWeightBold",
                  color: ignoredMaterials.has(v.material)
                    ? theme.palette.grey[200]
                    : "text.secondary",
                  mb: -0.5,
                }}
              >
                {shortNames[v.material] ?? v.material.slice(0, 4).toUpperCase()}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "fontWeightSemiBold",
                  color: ignoredMaterials.has(v.material)
                    ? theme.palette.grey[200]
                    : v.is_strike
                      ? theme.palette.error.main
                      : undefined,
                }}
              >
                {format(v.value)}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};
